import type {
  PricingRule,
  CartPricingContext,
  CartPricingResult,
  AppliedRule,
  DiscountType,
  ModificationAction,
  PriceBreakdownLine,
} from "./types";
import { evaluateConditionGroups } from "./condition-evaluator";

function isWithinValidityWindow(rule: PricingRule, bookingTime: Date): boolean {
  if (rule.validFrom) {
    const from = new Date(rule.validFrom);
    if (bookingTime < from) return false;
  }
  if (rule.validUntil) {
    const until = new Date(rule.validUntil);
    if (bookingTime > until) return false;
  }
  return true;
}

function legacyCartDiscountToAction(
  discountType: DiscountType,
  discountValue: number,
  ctx: CartPricingContext,
): ModificationAction | null {
  switch (discountType) {
    case "percentage":
      return { type: "percentage_decrease", value: discountValue };
    case "fixed_total":
      return { type: "fixed_decrease", value: discountValue };
    case "fixed_per_unit": {
      const totalQty = ctx.cart.reduce((sum, item) => sum + item.quantity, 0);
      return { type: "fixed_decrease", value: discountValue * totalQty };
    }
    case "price_override":
      return { type: "absolute_override", value: discountValue };
    default:
      return null;
  }
}

function applyCartModification(subtotal: number, action: ModificationAction): number {
  switch (action.type) {
    case "percentage_decrease":
      return -Math.min(subtotal, Math.round((subtotal * action.value) / 100));
    case "percentage_increase":
      return Math.round((subtotal * action.value) / 100);
    case "fixed_decrease":
      return -Math.min(subtotal, action.value);
    case "fixed_increase":
      return action.value;
    case "absolute_override": {
      const target = Math.max(0, action.value);
      return target - subtotal;
    }
    default:
      return 0;
  }
}

export function evaluateCartPricingRules(
  subtotal: number,
  rules: PricingRule[],
  context: CartPricingContext,
): CartPricingResult {
  const activeRules = rules
    .filter((r) => r.status === "enabled")
    .filter((r) => r.scope === "cart")
    .filter((r) => r.ruleType === "CONDITIONAL_DISCOUNT")
    .filter((r) => isWithinValidityWindow(r, context.bookingTime))
    .sort((a, b) => a.priority - b.priority);

  const appliedRules: AppliedRule[] = [];
  let bestNonStackableAdjustment = 0;
  let bestNonStackableRule: PricingRule | null = null;
  let stackedAdjustmentTotal = 0;

  for (const rule of activeRules) {
    const action =
      rule.modificationAction ??
      (rule.discountType && rule.discountValue != null
        ? legacyCartDiscountToAction(rule.discountType, rule.discountValue, context)
        : null);
    if (!action) continue;

    const { passes } = evaluateConditionGroups(rule.conditionGroups, rule.conditions, context);
    if (!passes) continue;

    const adjustment = applyCartModification(subtotal, action);
    if (adjustment === 0) continue;

    const isSurcharge = adjustment > 0;

    if (rule.stackable || isSurcharge) {
      stackedAdjustmentTotal += adjustment;
      appliedRules.push({
        ruleId: rule.id,
        ruleName: rule.name,
        ruleType: rule.ruleType,
        scope: "cart",
        adjustment,
        description: rule.description ?? rule.name,
      });
    } else if (adjustment < bestNonStackableAdjustment) {
      bestNonStackableAdjustment = adjustment;
      bestNonStackableRule = rule;
    }
  }

  if (bestNonStackableRule) {
    appliedRules.push({
      ruleId: bestNonStackableRule.id,
      ruleName: bestNonStackableRule.name,
      ruleType: bestNonStackableRule.ruleType,
      scope: "cart",
      adjustment: bestNonStackableAdjustment,
      description: bestNonStackableRule.description ?? bestNonStackableRule.name,
    });
  }

  const totalAdjustment = stackedAdjustmentTotal + bestNonStackableAdjustment;
  const grandTotal = Math.max(0, subtotal + totalAdjustment);

  const breakdown: PriceBreakdownLine[] = appliedRules.map((rule) => ({
    label: rule.ruleName,
    amount: rule.adjustment,
    type: rule.adjustment > 0 ? ("surcharge" as const) : ("cart_discount" as const),
  }));

  return {
    subtotal,
    cartDiscounts: appliedRules,
    cartAdjustment: totalAdjustment,
    grandTotal,
    breakdown,
  };
}
